import { useState, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffApi, kitchenStationsApi, preshiftConfigApi, shiftTemplatesApi, shiftInstancesApi, sectionsApi, ShiftInstance, ShiftTemplate, CreateShiftTemplateData, AddAssignmentData } from '../services/api';
import ShiftCalendar from '../components/ShiftCalendar';
import ShiftTemplateModal from '../components/ShiftTemplateModal';
import ShiftDetailPanel from '../components/ShiftDetailPanel';
import { wallClockRangeToISO } from '../lib/shiftTime';
import {
  Calendar,
  Clock,
  Users,
  ChefHat,
  MapPin,
  Utensils,
  AlertTriangle,
  Megaphone,
  Coffee,
  LogOut,
  Search,
  DollarSign,
  RefreshCw,
  UserPlus,
  Loader2,
  X,
  Plus,
  Trash2,
  Edit2,
  Eye,
  Flame,
  User,
  LayoutGrid,
  List,
  BookOpen,
  Save,
  Wine,
  ShieldAlert,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  CalendarDays,
  Sun,
  Moon,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';

// Tab configuration - simplified workflow
// Pre-Shift Setup and Assignments are now integrated into the Shift Setup page
type ShiftTab = 'calendar' | 'templates' | 'active' | 'history';

const tabs: Array<{ id: ShiftTab; label: string; icon: typeof Calendar }> = [
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'templates', label: 'Templates', icon: LayoutGrid },
  { id: 'active', label: 'Active Shifts', icon: Clock },
  { id: 'history', label: 'History', icon: CalendarDays },
];

// Role mappings
const ROLES = ['server', 'bartender', 'host', 'cook', 'manager', 'busser', 'dishwasher'];
const ROLE_LABELS: Record<string, string> = {
  server: 'Server',
  bartender: 'Bartender',
  host: 'Host',
  cook: 'Cook',
  manager: 'Manager',
  busser: 'Busser',
  dishwasher: 'Dishwasher',
};

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'high', label: 'High', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-700 border-slate-200' },
];

export default function ShiftsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ShiftTab>('calendar');

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Shift Management</h1>
            <p className="text-sm text-slate-500">Schedule, assign, and configure shifts</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton featureId="shifts" />
          <button
            onClick={() => queryClient.invalidateQueries()}
            className="flex items-center gap-2 px-3 py-2 text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-sm font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="border-b border-slate-200 bg-slate-50/50">
          <nav className="flex overflow-x-auto">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={clsx(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors',
                  activeTab === id
                    ? 'border-indigo-500 text-indigo-600 bg-white'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-white/50'
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4 md:p-6">
          {activeTab === 'calendar' && <CalendarTab />}
          {activeTab === 'templates' && <TemplatesTab />}
          {activeTab === 'active' && <ActiveShiftsTab />}
          {activeTab === 'history' && <HistoryTab />}
        </div>
      </div>
    </div>
  );
}

// ============================================
// SCHEDULE TAB
// ============================================
function ScheduleTab() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [shiftType, setShiftType] = useState<'all' | 'am' | 'pm'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [editingShift, setEditingShift] = useState<any>(null);

  // Shift creation form
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [shiftStart, setShiftStart] = useState('');
  const [shiftEnd, setShiftEnd] = useState('');
  const [shiftRole, setShiftRole] = useState('');
  const [shiftSection, setShiftSection] = useState('');
  const [shiftStation, setShiftStation] = useState('');
  const [shiftNotes, setShiftNotes] = useState('');

  // Generate form
  const [staffToGenerate, setStaffToGenerate] = useState<Array<{ employeeId: string; shiftType: 'am' | 'pm' | 'full' }>>([]);

  const { data: scheduleData, isLoading } = useQuery({
    queryKey: ['shift-schedule', selectedDate],
    queryFn: async () => {
      const response = await staffApi.getSchedule({ startDate: selectedDate, endDate: selectedDate });
      return response.data?.data || response.data || { shifts: [], stats: {} };
    },
  });

  // Get ALL active employees for shift creation
  const { data: availableStaff } = useQuery({
    queryKey: ['available-staff'],
    queryFn: async () => {
      const response = await staffApi.getAvailable();
      return response.data?.data || [];
    },
  });

  const { data: sectionsData } = useQuery({
    queryKey: ['sections'],
    queryFn: async () => {
      const response = await sectionsApi.getAll();
      return response.data?.data || response.data || [];
    },
  });

  const { data: stationsData } = useQuery({
    queryKey: ['kitchen-stations'],
    queryFn: async () => {
      const response = await kitchenStationsApi.getAll();
      return response.data?.data || [];
    },
  });

  const createShiftMutation = useMutation({
    mutationFn: (data: any) => staffApi.createShift(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-schedule'] });
      closeCreateModal();
    },
  });

  const updateShiftMutation = useMutation({
    mutationFn: ({ shiftId, data }: { shiftId: string; data: any }) => staffApi.updateShift(shiftId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-schedule'] });
      setEditingShift(null);
    },
  });

  const deleteShiftMutation = useMutation({
    mutationFn: (shiftId: string) => staffApi.deleteShift(shiftId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shift-schedule'] }),
  });

  const confirmShiftMutation = useMutation({
    mutationFn: (shiftId: string) => staffApi.confirmShift(shiftId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shift-schedule'] }),
  });

  const generateShiftsMutation = useMutation({
    mutationFn: (data: any) => staffApi.generateShifts(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-schedule'] });
      setShowGenerateModal(false);
      setStaffToGenerate([]);
    },
  });

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setSelectedEmployee(null);
    setShiftStart('');
    setShiftEnd('');
    setShiftRole('');
    setShiftSection('');
    setShiftStation('');
    setShiftNotes('');
  };

  const openCreateModal = () => {
    // Seed the datetime-local inputs with wall-clock defaults (9–5) for the
    // selected date. Build the string directly so the typed wall-clock is
    // preserved regardless of the browser timezone (canonical UTC-face rule).
    const dateStr = selectedDate.slice(0, 10);
    setShiftStart(`${dateStr}T09:00`);
    setShiftEnd(`${dateStr}T17:00`);
    setShowCreateModal(true);
  };

  const handleCreateShift = () => {
    if (!selectedEmployee || !shiftStart || !shiftEnd) return;

    // datetime-local values ("YYYY-MM-DDTHH:mm") are wall-clock; store the
    // entered clock-face verbatim as UTC (append seconds + Z).
    createShiftMutation.mutate({
      employeeId: selectedEmployee.id,
      scheduledStart: new Date(`${shiftStart}:00.000Z`).toISOString(),
      scheduledEnd: new Date(`${shiftEnd}:00.000Z`).toISOString(),
      role: shiftRole || selectedEmployee.primaryRole,
      section: shiftSection || undefined,
      station: shiftStation || undefined,
      notes: shiftNotes || undefined,
    });
  };

  const handleGenerateShifts = () => {
    generateShiftsMutation.mutate({
      date: selectedDate,
      staffToSchedule: staffToGenerate.length > 0 ? staffToGenerate : undefined,
    });
  };

  const toggleStaffForGenerate = (employeeId: string, shiftType: 'am' | 'pm' | 'full') => {
    setStaffToGenerate(prev => {
      const existing = prev.find(s => s.employeeId === employeeId);
      if (existing) {
        if (existing.shiftType === shiftType) {
          return prev.filter(s => s.employeeId !== employeeId);
        }
        return prev.map(s => s.employeeId === employeeId ? { ...s, shiftType } : s);
      }
      return [...prev, { employeeId, shiftType }];
    });
  };

  const shifts = scheduleData?.shifts || [];
  const stats = scheduleData?.stats || { total: 0, confirmed: 0, pending: 0 };
  const sections = Array.isArray(sectionsData) ? sectionsData : [];
  const stations = Array.isArray(stationsData) ? stationsData : [];
  const allStaff = Array.isArray(availableStaff) ? availableStaff : [];

  const filteredShifts = shifts.filter((shift: any) => {
    if (shiftType === 'all') return true;
    // Shift times are stored as UTC clock-face — read UTC hours.
    const hour = new Date(shift.startTime).getUTCHours();
    return shiftType === 'am' ? hour < 14 : hour >= 14;
  });

  const isBohRole = (role: string) => ['cook', 'line_cook', 'prep_cook', 'sous_chef', 'chef', 'kitchen', 'dishwasher'].includes(role?.toLowerCase());

  return (
    <div className="space-y-6">
      {/* Date & Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-end">
        <div className="flex-1">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Shift Type</label>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {[
              { id: 'all', label: 'All', icon: null },
              { id: 'am', label: 'AM', icon: Sun },
              { id: 'pm', label: 'PM', icon: Moon },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setShiftType(id as any)}
                className={clsx(
                  'px-4 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors',
                  shiftType === id
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                )}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          {/* Optional close-out for the day — EOD lives here, not in the nav */}
          {selectedDate <= new Date().toISOString().split('T')[0] && (
            <Link
              to="/end-of-day"
              className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors flex items-center gap-2"
            >
              <Moon className="w-4 h-4" />
              End of day
            </Link>
          )}
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Auto-Generate
          </button>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Shift
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4">
          <p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p>
          <p className="text-sm text-slate-500">Scheduled</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4">
          <p className="text-2xl font-bold text-emerald-700">{stats.confirmed || 0}</p>
          <p className="text-sm text-emerald-600">Confirmed</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-700">{stats.pending || 0}</p>
          <p className="text-sm text-amber-600">Pending</p>
        </div>
      </div>

      {/* Scheduled Shifts */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : filteredShifts.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No shifts scheduled</p>
          <p className="text-sm text-slate-500 mt-1">
            {selectedDate === new Date().toISOString().split('T')[0]
              ? 'No shifts scheduled for today'
              : 'No shifts scheduled for this date'}
          </p>
          <button
            onClick={openCreateModal}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            Create First Shift
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredShifts.map((shift: any) => (
            <div
              key={shift.id}
              className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group"
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                style={{ backgroundColor: getRoleColor(shift.role) }}
              >
                {shift.employeeName?.split(' ').map((n: string) => n[0]).join('') || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{shift.employeeName}</p>
                <p className="text-sm text-slate-500 capitalize">{shift.role}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">
                  {formatShiftTime(shift.startTime)} - {formatShiftTime(shift.endTime)}
                </p>
                <p className="text-xs text-slate-500">
                  {shift.station || shift.section || 'Unassigned'} · {shift.scheduledHours?.toFixed(1) || '?'}h
                </p>
              </div>
              <span
                className={clsx(
                  'px-2.5 py-1 text-xs font-medium rounded-full',
                  shift.status === 'confirmed' || shift.status === 'in_progress'
                    ? 'bg-emerald-100 text-emerald-700'
                    : shift.status === 'cancelled' || shift.status === 'no_show'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                )}
              >
                {shift.status === 'in_progress' ? 'In Progress' : (shift.status?.charAt(0).toUpperCase() + shift.status?.slice(1)) || 'Scheduled'}
              </span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {shift.status === 'scheduled' && (
                  <button
                    onClick={() => confirmShiftMutation.mutate(shift.id)}
                    title="Confirm Shift"
                    className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setEditingShift(shift)}
                  title="Edit Shift"
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Delete shift for ${shift.employeeName}?`)) {
                      deleteShiftMutation.mutate(shift.id);
                    }
                  }}
                  title="Delete Shift"
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Shift Modal */}
      {showCreateModal && (
        <Modal onClose={closeCreateModal} title="Create Shift">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Employee</label>
              {selectedEmployee ? (
                <div className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium"
                      style={{ backgroundColor: getRoleColor(selectedEmployee.primaryRole) }}
                    >
                      {selectedEmployee.firstName?.[0]}{selectedEmployee.lastName?.[0]}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{selectedEmployee.firstName} {selectedEmployee.lastName}</p>
                      <p className="text-xs text-slate-500 capitalize">{selectedEmployee.primaryRole}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedEmployee(null)} className="text-indigo-600 hover:text-indigo-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                  {allStaff.length === 0 ? (
                    <p className="p-3 text-sm text-slate-500 text-center">No active staff found</p>
                  ) : (
                    allStaff.map((emp: any) => (
                      <button
                        key={emp.id}
                        onClick={() => {
                          setSelectedEmployee(emp);
                          setShiftRole(emp.primaryRole);
                        }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left border-b border-slate-100 last:border-0"
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium"
                          style={{ backgroundColor: getRoleColor(emp.primaryRole) }}
                        >
                          {emp.firstName?.[0]}{emp.lastName?.[0]}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm text-slate-900">{emp.firstName} {emp.lastName}</p>
                          <p className="text-xs text-slate-500 capitalize">{emp.primaryRole}</p>
                        </div>
                        {emp.isClockedIn && (
                          <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full">On Clock</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Time</label>
                <input
                  type="datetime-local"
                  value={shiftStart}
                  onChange={(e) => setShiftStart(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Time</label>
                <input
                  type="datetime-local"
                  value={shiftEnd}
                  onChange={(e) => setShiftEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <select
                value={shiftRole}
                onChange={(e) => setShiftRole(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">Select role</option>
                {ROLES.map(role => (
                  <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                ))}
              </select>
            </div>

            {selectedEmployee && !isBohRole(shiftRole || selectedEmployee?.primaryRole) && sections.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Section (optional)</label>
                <select
                  value={shiftSection}
                  onChange={(e) => setShiftSection(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="">No section assigned</option>
                  {sections.map((s: any) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedEmployee && isBohRole(shiftRole || selectedEmployee?.primaryRole) && stations.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Station (optional)</label>
                <select
                  value={shiftStation}
                  onChange={(e) => setShiftStation(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="">No station assigned</option>
                  {stations.map((s: any) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
              <textarea
                value={shiftNotes}
                onChange={(e) => setShiftNotes(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                rows={2}
                placeholder="Special instructions..."
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={closeCreateModal}
              className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateShift}
              disabled={!selectedEmployee || !shiftStart || !shiftEnd || createShiftMutation.isPending}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {createShiftMutation.isPending ? 'Creating...' : 'Create Shift'}
            </button>
          </div>
        </Modal>
      )}

      {/* Auto-Generate Modal */}
      {showGenerateModal && (
        <Modal onClose={() => { setShowGenerateModal(false); setStaffToGenerate([]); }} title="Auto-Generate Shifts">
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Generate shifts for <strong>{new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</strong> based on restaurant operating hours.
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select staff to schedule <span className="text-slate-400 font-normal">(leave empty for all)</span>
              </label>
              <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
                {allStaff.map((emp: any) => {
                  const selected = staffToGenerate.find(s => s.employeeId === emp.id);
                  return (
                    <div key={emp.id} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium"
                          style={{ backgroundColor: getRoleColor(emp.primaryRole) }}
                        >
                          {emp.firstName?.[0]}{emp.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-slate-900">{emp.firstName} {emp.lastName}</p>
                          <p className="text-xs text-slate-500 capitalize">{emp.primaryRole}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {(['am', 'pm', 'full'] as const).map(type => (
                          <button
                            key={type}
                            onClick={() => toggleStaffForGenerate(emp.id, type)}
                            className={clsx(
                              'px-2 py-1 text-xs font-medium rounded transition-colors',
                              selected?.shiftType === type
                                ? 'bg-indigo-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            )}
                          >
                            {type.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {staffToGenerate.length > 0 && (
              <p className="text-sm text-indigo-600">
                {staffToGenerate.length} staff selected
              </p>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => { setShowGenerateModal(false); setStaffToGenerate([]); }}
              className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerateShifts}
              disabled={generateShiftsMutation.isPending}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {generateShiftsMutation.isPending ? 'Generating...' : 'Generate Shifts'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Shift Modal */}
      {editingShift && (
        <Modal onClose={() => setEditingShift(null)} title="Edit Shift">
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                style={{ backgroundColor: getRoleColor(editingShift.role) }}
              >
                {editingShift.employeeName?.split(' ').map((n: string) => n[0]).join('') || '?'}
              </div>
              <div>
                <p className="font-medium text-slate-900">{editingShift.employeeName}</p>
                <p className="text-sm text-slate-500 capitalize">{editingShift.role}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select
                value={editingShift.status}
                onChange={(e) => setEditingShift({ ...editingShift, status: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="scheduled">Scheduled</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="no_show">No Show</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <textarea
                value={editingShift.notes || ''}
                onChange={(e) => setEditingShift({ ...editingShift, notes: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                rows={2}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setEditingShift(null)}
              className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={() => updateShiftMutation.mutate({
                shiftId: editingShift.id,
                data: { status: editingShift.status, notes: editingShift.notes },
              })}
              disabled={updateShiftMutation.isPending}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {updateShiftMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================
// ACTIVE SHIFTS TAB
// ============================================
function ActiveShiftsTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [showNoteModal, setShowNoteModal] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [subTab, setSubTab] = useState<'instances' | 'staff' | 'not-clocked'>('instances');

  // Fetch active shift instances (status = 'in_progress')
  const today = new Date().toISOString().split('T')[0];
  const { data: activeInstancesData, isLoading: instancesLoading } = useQuery({
    queryKey: ['active-shift-instances', today],
    queryFn: async () => {
      const response = await shiftInstancesApi.getByDateRange(today, today, 'in_progress');
      return response.data?.data || response.data || [];
    },
    refetchInterval: 30000,
  });

  const { data: shiftsData, isLoading } = useQuery({
    queryKey: ['active-shifts'],
    queryFn: async () => {
      const response = await staffApi.getActiveShifts();
      return response.data?.data || response.data || [];
    },
    refetchInterval: 30000,
  });

  const { data: sectionsData } = useQuery({
    queryKey: ['sections'],
    queryFn: async () => {
      const response = await sectionsApi.getAll();
      return response.data?.data || response.data || [];
    },
  });

  const { data: scheduledData, isLoading: scheduledLoading } = useQuery({
    queryKey: ['scheduled-not-clocked-in'],
    queryFn: async () => {
      const response = await staffApi.getScheduledNotClockedIn();
      return response.data?.data || response.data || [];
    },
    enabled: subTab === 'not-clocked',
  });

  const clockOutMutation = useMutation({
    mutationFn: (employeeId: string) => staffApi.managerClockOut(employeeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['active-shifts'] }),
  });

  const clockOutAllMutation = useMutation({
    mutationFn: () => staffApi.managerClockOutAll(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['active-shifts'] });
      queryClient.invalidateQueries({ queryKey: ['shift-history'] });
      const data = response.data;
      if (data?.clockedOut > 0) {
        alert(`Successfully clocked out ${data.clockedOut} employee(s)`);
      }
    },
  });

  const startBreakMutation = useMutation({
    mutationFn: (employeeId: string) => staffApi.managerStartBreak(employeeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['active-shifts'] }),
  });

  const endBreakMutation = useMutation({
    mutationFn: (employeeId: string) => staffApi.managerEndBreak(employeeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['active-shifts'] }),
  });

  const addNoteMutation = useMutation({
    mutationFn: ({ employeeId, note }: { employeeId: string; note: string }) =>
      staffApi.addShiftNote(employeeId, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-shifts'] });
      setShowNoteModal(null);
      setNoteText('');
    },
  });

  const managerClockInMutation = useMutation({
    mutationFn: ({ employeeId, sectionId }: { employeeId: string; sectionId?: string }) =>
      staffApi.managerClockIn(employeeId, sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-shifts'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-not-clocked-in'] });
    },
  });

  const activeInstances = Array.isArray(activeInstancesData) ? activeInstancesData : [];
  const shifts = Array.isArray(shiftsData) ? shiftsData : [];
  const sections = Array.isArray(sectionsData) ? sectionsData : [];
  const scheduledEmployees = Array.isArray(scheduledData) ? scheduledData : [];
  const roles = ['all', 'server', 'cook', 'host', 'manager', 'bartender', 'busser'];

  const filteredShifts = shifts.filter((shift: any) => {
    const matchesSearch = shift.employeeName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || shift.role?.toLowerCase() === selectedRole;
    return matchesSearch && matchesRole;
  });

  const totalSales = filteredShifts.reduce((sum: number, s: any) => sum + (s.salesCents || 0), 0);
  const totalTips = filteredShifts.reduce((sum: number, s: any) => sum + (s.tipsCents || 0), 0);
  const totalOnBreak = filteredShifts.filter((s: any) => s.isOnBreak).length;
  const needsBreak = filteredShifts.filter((s: any) => !s.isOnBreak && s.lastBreakHoursAgo >= 4);

  // Map clocked-in staff by employeeId for cross-reference
  const clockedInByEmployeeId = useMemo(() => {
    const map = new Map<string, any>();
    for (const shift of shifts) {
      map.set(shift.employeeId, shift);
    }
    return map;
  }, [shifts]);

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setSubTab('instances')}
          className={clsx(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
            subTab === 'instances'
              ? 'bg-red-100 text-red-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          )}
        >
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Active Shifts
            {activeInstances.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-red-200 text-red-800 rounded-full animate-pulse">
                {activeInstances.length} LIVE
              </span>
            )}
          </span>
        </button>
        <button
          onClick={() => setSubTab('staff')}
          className={clsx(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
            subTab === 'staff'
              ? 'bg-indigo-100 text-indigo-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          )}
        >
          <span className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            On Clock
            <span className="px-1.5 py-0.5 text-xs bg-indigo-200 text-indigo-800 rounded-full">
              {shifts.length}
            </span>
          </span>
        </button>
        <button
          onClick={() => setSubTab('not-clocked')}
          className={clsx(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
            subTab === 'not-clocked'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          )}
        >
          <span className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Not Clocked In
            {scheduledEmployees.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-amber-200 text-amber-800 rounded-full">
                {scheduledEmployees.length}
              </span>
            )}
          </span>
        </button>

        {/* Clock Out All Button */}
        {shifts.length > 0 && (
          <button
            onClick={() => {
              if (confirm(`Clock out all ${shifts.length} active employees? This cannot be undone.`)) {
                clockOutAllMutation.mutate();
              }
            }}
            disabled={clockOutAllMutation.isPending}
            className="ml-auto px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            {clockOutAllMutation.isPending ? 'Clocking Out...' : `Clock Out All (${shifts.length})`}
          </button>
        )}
      </div>

      {/* Active Shift Instances View */}
      {subTab === 'instances' && (
        <>
          {instancesLoading ? (
            <LoadingSpinner />
          ) : activeInstances.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="No Active Shifts"
              description="No shifts are currently in progress. Start a shift from the calendar to see it here."
            />
          ) : (
            <div className="space-y-6">
              {activeInstances.map((instance: ShiftInstance) => {
                const instanceAssignments = instance.assignments || [];
                const instanceStartTime = new Date(instance.startTime);
                const instanceEndTime = new Date(instance.endTime);

                // Separate clocked-in vs not clocked-in staff
                const clockedIn = instanceAssignments.filter((a: any) => {
                  const clockStatus = clockedInByEmployeeId.get(a.employeeId);
                  return !!clockStatus;
                });
                const notClockedIn = instanceAssignments.filter((a: any) => {
                  const clockStatus = clockedInByEmployeeId.get(a.employeeId);
                  return !clockStatus;
                });

                return (
                  <div
                    key={instance.id}
                    className="bg-white border-2 border-red-200 rounded-xl overflow-hidden shadow-sm"
                  >
                    {/* Shift Instance Header */}
                    <div
                      className="px-4 py-3 flex items-center justify-between"
                      style={{ backgroundColor: `${instance.color || '#EF4444'}15` }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full animate-pulse"
                          style={{ backgroundColor: instance.color || '#EF4444' }}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-900">{instance.name}</h4>
                            <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase bg-red-500 text-white rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                              LIVE
                            </span>
                          </div>
                          <p className="text-sm text-slate-500">
                            {instanceStartTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })} - {instanceEndTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })}
                            {' · '}{instanceAssignments.length} staff assigned
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/shifts/${instance.id}/setup`)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        Open Setup
                      </button>
                    </div>

                    {/* Staff Status */}
                    <div className="p-4">
                      {/* Clocked In Section */}
                      {clockedIn.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Clocked In ({clockedIn.length})
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {clockedIn.map((assignment: any) => {
                              const clockStatus = clockedInByEmployeeId.get(assignment.employeeId);
                              const section = sections.find((s: any) => s.id === assignment.sectionId);
                              return (
                                <div
                                  key={assignment.id}
                                  className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg"
                                >
                                  <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                                    style={{ backgroundColor: getRoleColor(assignment.role) }}
                                  >
                                    {assignment.employee?.firstName?.[0]}{assignment.employee?.lastName?.[0]}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">
                                      {assignment.employee?.firstName} {assignment.employee?.lastName?.[0]}.
                                    </p>
                                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                      <span className="capitalize">{assignment.role}</span>
                                      {section && (
                                        <>
                                          <span>·</span>
                                          <span style={{ color: section.color }}>{section.name}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {clockStatus?.isOnBreak && (
                                    <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-full">
                                      Break
                                    </span>
                                  )}
                                  <span className="text-[10px] text-emerald-600 font-medium">
                                    {formatHours(clockStatus?.hoursWorked || 0)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Not Clocked In Section */}
                      {notClockedIn.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Not Yet Clocked In ({notClockedIn.length})
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {notClockedIn.map((assignment: any) => {
                              const section = sections.find((s: any) => s.id === assignment.sectionId);
                              const scheduledStart = new Date(assignment.scheduledStart);
                              const isLate = Date.now() > scheduledStart.getTime();
                              return (
                                <div
                                  key={assignment.id}
                                  className={clsx(
                                    'flex items-center gap-2 p-2 border rounded-lg',
                                    isLate
                                      ? 'bg-red-50 border-red-200'
                                      : 'bg-amber-50 border-amber-200'
                                  )}
                                >
                                  <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 opacity-60"
                                    style={{ backgroundColor: getRoleColor(assignment.role) }}
                                  >
                                    {assignment.employee?.firstName?.[0]}{assignment.employee?.lastName?.[0]}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-700 truncate">
                                      {assignment.employee?.firstName} {assignment.employee?.lastName?.[0]}.
                                    </p>
                                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                      <span className="capitalize">{assignment.role}</span>
                                      {section && (
                                        <>
                                          <span>·</span>
                                          <span>{section.name}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {isLate ? (
                                    <span className="px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 rounded-full font-medium">
                                      Late
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-amber-600">
                                      {scheduledStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => managerClockInMutation.mutate({ employeeId: assignment.employeeId, sectionId: assignment.sectionId })}
                                    disabled={managerClockInMutation.isPending}
                                    className="p-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                                    title="Clock In"
                                  >
                                    <UserPlus className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {instanceAssignments.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-4">
                          No staff assigned to this shift yet.{' '}
                          <button
                            onClick={() => navigate(`/shifts/${instance.id}/setup`)}
                            className="text-indigo-600 hover:underline"
                          >
                            Open setup to add staff
                          </button>
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {subTab === 'staff' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Users} label="On Clock" value={filteredShifts.length} color="indigo" />
            <StatCard icon={DollarSign} label="Sales" value={formatCurrency(totalSales)} color="emerald" />
            <StatCard icon={Coffee} label="On Break" value={totalOnBreak} color="amber" />
            <StatCard icon={DollarSign} label="Tips" value={formatCurrency(totalTips)} color="blue" />
          </div>

          {/* Break Alert */}
          {needsBreak.length > 0 && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Break Reminder</p>
                <p className="text-sm text-amber-700">
                  {needsBreak.length} staff {needsBreak.length > 1 ? 'have' : 'has'} been working 4+ hours:{' '}
                  {needsBreak.map((s: any) => s.employeeName).join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search staff..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {roles.map((role) => (
                <button
                  key={role}
                  onClick={() => setSelectedRole(role)}
                  className={clsx(
                    'px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
                    selectedRole === role
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  )}
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Shifts List */}
          {isLoading ? (
            <LoadingSpinner />
          ) : filteredShifts.length === 0 ? (
            <EmptyState
              icon={Clock}
              title="No Active Shifts"
              description={searchQuery || selectedRole !== 'all' ? 'No staff match your filters' : 'No staff are currently clocked in'}
            />
          ) : (
            <div className="space-y-2">
              {filteredShifts.map((shift: any) => (
                <div
                  key={shift.id}
                  className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                    style={{ backgroundColor: getRoleColor(shift.role) }}
                  >
                    {shift.employeeName?.split(' ').map((n: string) => n[0]).join('').slice(0, 2) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{shift.employeeName}</p>
                    <p className="text-sm text-slate-500 capitalize">{shift.role}</p>
                  </div>
                  {shift.sectionName && (
                    <div className="hidden sm:flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: shift.sectionColor || '#94a3b8' }}
                      />
                      <span className="text-sm text-slate-600">{shift.sectionName}</span>
                    </div>
                  )}
                  <div className="text-right hidden md:block">
                    <p className="text-sm font-medium text-slate-900">{formatHours(shift.hoursWorked || 0)}</p>
                    <p className="text-xs text-slate-500">{formatCurrency(shift.salesCents || 0)}</p>
                  </div>
                  <ShiftStatus shift={shift} />
                  <div className="flex items-center gap-1">
                    {shift.isOnBreak ? (
                      <ActionButton
                        icon={Coffee}
                        onClick={() => endBreakMutation.mutate(shift.employeeId)}
                        title="End Break"
                        color="amber"
                      />
                    ) : (
                      <ActionButton
                        icon={Coffee}
                        onClick={() => startBreakMutation.mutate(shift.employeeId)}
                        title="Start Break"
                      />
                    )}
                    <ActionButton
                      icon={MessageSquare}
                      onClick={() => setShowNoteModal(shift.employeeId)}
                      title="Add Note"
                    />
                    <ActionButton
                      icon={LogOut}
                      onClick={() => {
                        if (confirm(`Clock out ${shift.employeeName}?`)) {
                          clockOutMutation.mutate(shift.employeeId);
                        }
                      }}
                      title="Clock Out"
                      color="red"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {subTab === 'not-clocked' && (
        <>
          {scheduledLoading ? (
            <LoadingSpinner />
          ) : scheduledEmployees.length === 0 ? (
            <EmptyState
              icon={Users}
              title="All Staff Clocked In"
              description="Everyone scheduled for today has already clocked in"
            />
          ) : (
            <div className="space-y-2">
              {scheduledEmployees.map((employee: any) => {
                const scheduledStart = new Date(employee.scheduledStart);
                const minutesLate = Math.floor((Date.now() - scheduledStart.getTime()) / 60000);
                const isLate = minutesLate > 0;

                return (
                  <div
                    key={employee.id}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: getRoleColor(employee.role) }}
                    >
                      {employee.name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{employee.name}</p>
                      <p className="text-sm text-slate-500 capitalize">{employee.role}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-700">
                        {formatShiftTime(employee.scheduledStart)} - {formatShiftTime(employee.scheduledEnd)}
                      </p>
                    </div>
                    {isLate ? (
                      <span className="px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                        {minutesLate}m late
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                        Scheduled
                      </span>
                    )}
                    <button
                      onClick={() => managerClockInMutation.mutate({ employeeId: employee.id })}
                      disabled={managerClockInMutation.isPending}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-1.5"
                    >
                      <UserPlus className="w-4 h-4" />
                      Clock In
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Note Modal */}
      {showNoteModal && (
        <Modal onClose={() => { setShowNoteModal(null); setNoteText(''); }} title="Add Shift Note">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter a note for this shift..."
            className="w-full h-28 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => { setShowNoteModal(null); setNoteText(''); }}
              className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={() => noteText.trim() && addNoteMutation.mutate({ employeeId: showNoteModal, note: noteText.trim() })}
              disabled={!noteText.trim() || addNoteMutation.isPending}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {addNoteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Add Note'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================
// ASSIGNMENTS TAB
// ============================================
function AssignmentsTab() {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'floor' | 'list' | 'kitchen'>('floor');
  const [showAssignModal, setShowAssignModal] = useState<any>(null);
  const [showCreateStationModal, setShowCreateStationModal] = useState(false);
  const [newStationName, setNewStationName] = useState('');
  const [newStationColor, setNewStationColor] = useState('#6366f1');

  const { data: overviewData, isLoading } = useQuery({
    queryKey: ['shift-overview'],
    queryFn: async () => {
      const response = await staffApi.getShiftOverview();
      return response.data?.data;
    },
    refetchInterval: 30000,
  });

  // Get ALL active employees for assignments (not just clocked-in)
  const { data: allStaffData } = useQuery({
    queryKey: ['available-staff-for-assignment'],
    queryFn: async () => {
      const response = await staffApi.getAvailable();
      return response.data?.data || [];
    },
  });

  const assignServerMutation = useMutation({
    mutationFn: ({ employeeId, sectionId }: { employeeId: string; sectionId: string }) =>
      staffApi.reassignSection(employeeId, sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-overview'] });
      setShowAssignModal(null);
    },
  });

  const assignCookMutation = useMutation({
    mutationFn: ({ stationId, employeeId }: { stationId: string; employeeId: string }) =>
      kitchenStationsApi.assign(stationId, employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-overview'] });
      setShowAssignModal(null);
    },
  });

  const unassignCookMutation = useMutation({
    mutationFn: (stationId: string) => kitchenStationsApi.unassign(stationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shift-overview'] }),
  });

  const createStationMutation = useMutation({
    mutationFn: (data: { name: string; color: string }) => kitchenStationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-overview'] });
      setShowCreateStationModal(false);
      setNewStationName('');
    },
  });

  const summary = overviewData?.summary;
  const sections = overviewData?.frontOfHouse?.sections || [];
  const stations = overviewData?.backOfHouse?.stations || [];
  const unassignedServers = overviewData?.frontOfHouse?.unassignedServers || [];
  const unassignedCooks = overviewData?.backOfHouse?.unassignedCooks || [];

  // Get all active staff for section/station assignment (show everyone, not just clocked-in)
  const allStaff = Array.isArray(allStaffData) ? allStaffData : [];
  const fohRoles = ['server', 'bartender', 'host', 'busser'];
  const bohRoles = ['cook', 'line_cook', 'prep_cook', 'sous_chef', 'chef', 'kitchen', 'dishwasher'];

  // Get assigned server/cook IDs so we can show who's already assigned
  const assignedServerIds = new Set(sections.filter((s: any) => s.assignedServer).map((s: any) => s.assignedServer.id));
  const assignedCookIds = new Set(stations.filter((s: any) => s.assignedCook).map((s: any) => s.assignedCook.id));

  // All FOH staff available for assignment (filter by role, show assignment status)
  const allFohStaff = allStaff.filter((emp: any) => {
    const role = emp.primaryRole?.toLowerCase() || '';
    return fohRoles.some(r => role.includes(r));
  }).map((emp: any) => ({
    ...emp,
    isAssigned: assignedServerIds.has(emp.id),
  }));

  // All BOH staff available for assignment
  const allBohStaff = allStaff.filter((emp: any) => {
    const role = emp.primaryRole?.toLowerCase() || '';
    return bohRoles.some(r => role.includes(r));
  }).map((emp: any) => ({
    ...emp,
    isAssigned: assignedCookIds.has(emp.id),
  }));

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard icon={Users} label="On Clock" value={summary?.totalClockedIn || 0} color="indigo" subtext={`FOH: ${summary?.fohClockedIn || 0} · BOH: ${summary?.bohClockedIn || 0}`} />
        <StatCard
          icon={MapPin}
          label="Sections"
          value={`${summary?.sectionsAssigned || 0}/${summary?.sectionsTotal || 0}`}
          color="emerald"
          progress={(summary?.sectionsTotal ? (summary.sectionsAssigned / summary.sectionsTotal) * 100 : 0)}
        />
        <StatCard
          icon={Flame}
          label="Stations"
          value={`${summary?.stationsAssigned || 0}/${summary?.stationsTotal || 0}`}
          color="amber"
          progress={(summary?.stationsTotal ? (summary.stationsAssigned / summary.stationsTotal) * 100 : 0)}
        />
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <ViewToggle id="floor" label="Floor Plan" icon={LayoutGrid} current={viewMode} onChange={setViewMode} />
        <ViewToggle id="list" label="List" icon={List} current={viewMode} onChange={setViewMode} />
        <ViewToggle id="kitchen" label="Kitchen" icon={ChefHat} current={viewMode} onChange={setViewMode} />
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : viewMode === 'floor' ? (
        <FloorPlanView sections={sections} onAssign={setShowAssignModal} />
      ) : viewMode === 'list' ? (
        <AssignmentListView sections={sections} onAssign={setShowAssignModal} />
      ) : (
        <KitchenView
          stations={stations}
          onAssign={setShowAssignModal}
          onUnassign={(stationId) => unassignCookMutation.mutate(stationId)}
          onCreate={() => setShowCreateStationModal(true)}
        />
      )}

      {/* Unassigned Staff Alerts */}
      {viewMode !== 'kitchen' && unassignedServers.length > 0 && (
        <UnassignedAlert type="servers" staff={unassignedServers} />
      )}
      {viewMode === 'kitchen' && unassignedCooks.length > 0 && (
        <UnassignedAlert type="cooks" staff={unassignedCooks} />
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <Modal
          onClose={() => setShowAssignModal(null)}
          title={`Assign ${showAssignModal.type === 'station' ? 'Cook' : 'Server'} to ${showAssignModal.name}`}
        >
          {(() => {
            const staffList = showAssignModal.type === 'station' ? allBohStaff : allFohStaff;
            if (staffList.length === 0) {
              return <p className="text-slate-500 text-center py-6">No active {showAssignModal.type === 'station' ? 'kitchen' : 'front of house'} staff found</p>;
            }
            return (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {staffList.map((staff: any) => (
                  <button
                    key={staff.id}
                    onClick={() => {
                      if (showAssignModal.type === 'station') {
                        assignCookMutation.mutate({ stationId: showAssignModal.id, employeeId: staff.id });
                      } else {
                        assignServerMutation.mutate({ sectionId: showAssignModal.id, employeeId: staff.id });
                      }
                    }}
                    disabled={assignServerMutation.isPending || assignCookMutation.isPending}
                    className={clsx(
                      'w-full flex items-center gap-3 p-3 border rounded-xl transition-colors',
                      staff.isAssigned
                        ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
                        : 'border-slate-200 hover:bg-slate-50'
                    )}
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: getRoleColor(staff.primaryRole) }}
                    >
                      {staff.firstName?.[0]}{staff.lastName?.[0]}
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-medium text-slate-900">{staff.firstName} {staff.lastName}</p>
                      <p className="text-xs text-slate-500 capitalize">{staff.primaryRole}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {staff.isClockedIn ? (
                        <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full">On Clock</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-500 rounded-full">Off Clock</span>
                      )}
                      {staff.isAssigned && (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">Assigned</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            );
          })()}
        </Modal>
      )}

      {/* Create Station Modal */}
      {showCreateStationModal && (
        <Modal onClose={() => setShowCreateStationModal(false)} title="Create Kitchen Station">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Station Name</label>
              <input
                type="text"
                value={newStationName}
                onChange={(e) => setNewStationName(e.target.value)}
                placeholder="e.g., Grill, Fry, Saute"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
              <input
                type="color"
                value={newStationColor}
                onChange={(e) => setNewStationColor(e.target.value)}
                className="w-full h-10 rounded-lg cursor-pointer border border-slate-200"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => setShowCreateStationModal(false)} className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200">
              Cancel
            </button>
            <button
              onClick={() => createStationMutation.mutate({ name: newStationName, color: newStationColor })}
              disabled={!newStationName || createStationMutation.isPending}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {createStationMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================
// PRE-SHIFT TAB
// ============================================
function PreShiftTab() {
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState<'specials' | 'eightysix' | 'announcements' | 'preview'>('specials');

  // Specials state
  const [showAddSpecial, setShowAddSpecial] = useState(false);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const [selectedMenuItem, setSelectedMenuItem] = useState<any>(null);
  const [specialPrice, setSpecialPrice] = useState('');
  const [specialDescription, setSpecialDescription] = useState('');

  // 86'd state
  const [eightySixSearch, setEightySixSearch] = useState('');

  // Announcements state
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    priority: 'normal',
    targetRoles: [] as string[],
    expiresAt: '',
  });

  // Preview state
  const [previewRole, setPreviewRole] = useState('server');

  // Queries
  const { data: specialsData, isLoading: specialsLoading } = useQuery({
    queryKey: ['preshift-specials'],
    queryFn: async () => {
      const response = await preshiftConfigApi.getSpecials();
      return response.data?.data || [];
    },
    enabled: subTab === 'specials',
  });

  const { data: eightySixData, isLoading: eightySixLoading } = useQuery({
    queryKey: ['preshift-eightysix'],
    queryFn: async () => {
      const response = await preshiftConfigApi.getEightySixItems();
      const data = response.data?.data || { eightySixItems: [], availableItems: [] };
      return [
        ...(data.eightySixItems || []).map((item: any) => ({ ...item, is86d: true })),
        ...(data.availableItems || []).map((item: any) => ({ ...item, is86d: false })),
      ];
    },
    enabled: subTab === 'eightysix',
  });

  const { data: announcementsData, isLoading: announcementsLoading } = useQuery({
    queryKey: ['preshift-announcements'],
    queryFn: async () => {
      const response = await preshiftConfigApi.getAnnouncements();
      return response.data?.data || [];
    },
    enabled: subTab === 'announcements',
  });

  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey: ['preshift-preview', previewRole],
    queryFn: async () => {
      const response = await preshiftConfigApi.getPreview(previewRole);
      return response.data?.data || {};
    },
    enabled: subTab === 'preview',
  });

  const { data: menuSearchResults, isLoading: menuSearchLoading } = useQuery({
    queryKey: ['menu-search', menuSearchQuery],
    queryFn: async () => {
      if (!menuSearchQuery.trim()) return [];
      const response = await preshiftConfigApi.searchMenuItems(menuSearchQuery);
      return response.data?.data || [];
    },
    enabled: menuSearchQuery.length >= 2,
  });

  // Mutations
  const addSpecialMutation = useMutation({
    mutationFn: (data: any) => preshiftConfigApi.addSpecial(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preshift-specials'] });
      setShowAddSpecial(false);
      setSelectedMenuItem(null);
      setSpecialPrice('');
      setSpecialDescription('');
      setMenuSearchQuery('');
    },
  });

  const removeSpecialMutation = useMutation({
    mutationFn: (id: string) => preshiftConfigApi.removeSpecial(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['preshift-specials'] }),
  });

  const toggleEightySixMutation = useMutation({
    mutationFn: ({ menuItemId, isAvailable }: { menuItemId: string; isAvailable: boolean }) =>
      preshiftConfigApi.toggleEightySix(menuItemId, { isAvailable }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['preshift-eightysix'] }),
  });

  const createAnnouncementMutation = useMutation({
    mutationFn: (data: any) => preshiftConfigApi.createAnnouncement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preshift-announcements'] });
      setShowAddAnnouncement(false);
      setAnnouncementForm({ title: '', message: '', priority: 'normal', targetRoles: [], expiresAt: '' });
    },
  });

  const deleteAnnouncementMutation = useMutation({
    mutationFn: (id: string) => preshiftConfigApi.deleteAnnouncement(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['preshift-announcements'] }),
  });

  const filteredEightySixItems = (eightySixData || []).filter((item: any) =>
    item.name?.toLowerCase().includes(eightySixSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 overflow-x-auto">
        {[
          { id: 'specials', label: 'Specials', icon: Utensils },
          { id: 'eightysix', label: "86'd Items", icon: AlertTriangle },
          { id: 'announcements', label: 'Announcements', icon: Megaphone },
          { id: 'preview', label: 'Preview', icon: Eye },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSubTab(id as any)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2',
              subTab === id
                ? 'bg-indigo-100 text-indigo-700'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Specials */}
      {subTab === 'specials' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Highlight menu items as daily specials</p>
            <button onClick={() => setShowAddSpecial(true)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Add Special
            </button>
          </div>

          {specialsLoading ? <LoadingSpinner /> : !specialsData?.length ? (
            <EmptyState icon={Utensils} title="No Specials Today" description="Add menu items as daily specials" />
          ) : (
            <div className="space-y-2">
              {specialsData.map((special: any) => (
                <div key={special.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-900">{special.menuItem?.name}</p>
                    {special.description && <p className="text-sm text-slate-500">{special.description}</p>}
                    <div className="flex gap-3 mt-1 text-sm">
                      <span className="text-slate-500">Regular: ${((special.menuItem?.priceCents || 0) / 100).toFixed(2)}</span>
                      {special.specialPriceCents && (
                        <span className="text-indigo-600 font-medium">Special: ${(special.specialPriceCents / 100).toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => removeSpecialMutation.mutate(special.id)} className="p-2 text-slate-400 hover:text-red-600 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add Special Modal */}
          {showAddSpecial && (
            <Modal onClose={() => { setShowAddSpecial(false); setSelectedMenuItem(null); setMenuSearchQuery(''); }} title="Add Daily Special">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Search Menu Item</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={menuSearchQuery}
                      onChange={(e) => setMenuSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
                      placeholder="Search by name..."
                    />
                  </div>
                  {menuSearchQuery.length >= 2 && (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                      {menuSearchLoading ? (
                        <div className="p-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-indigo-600" /></div>
                      ) : !menuSearchResults?.length ? (
                        <div className="p-3 text-center text-sm text-slate-500">No items found</div>
                      ) : (
                        menuSearchResults.map((item: any) => (
                          <button
                            key={item.id}
                            onClick={() => { setSelectedMenuItem(item); setMenuSearchQuery(''); }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 flex justify-between text-sm"
                          >
                            <span>{item.name}</span>
                            <span className="text-slate-500">${(item.priceCents / 100).toFixed(2)}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {selectedMenuItem && (
                  <div className="p-3 bg-indigo-50 rounded-lg flex justify-between items-center">
                    <span className="font-medium text-indigo-900">{selectedMenuItem.name}</span>
                    <button onClick={() => setSelectedMenuItem(null)} className="text-indigo-600"><X className="w-4 h-4" /></button>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Special Price (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={specialPrice}
                    onChange={(e) => setSpecialPrice(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Leave empty for regular price"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                  <textarea
                    value={specialDescription}
                    onChange={(e) => setSpecialDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                    rows={2}
                    placeholder="e.g., Chef's special preparation..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => { setShowAddSpecial(false); setSelectedMenuItem(null); }} className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200">
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedMenuItem) {
                      addSpecialMutation.mutate({
                        menuItemId: selectedMenuItem.id,
                        specialPriceCents: specialPrice ? Math.round(parseFloat(specialPrice) * 100) : undefined,
                        description: specialDescription || undefined,
                      });
                    }
                  }}
                  disabled={!selectedMenuItem || addSpecialMutation.isPending}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  {addSpecialMutation.isPending ? 'Adding...' : 'Add Special'}
                </button>
              </div>
            </Modal>
          )}
        </div>
      )}

      {/* 86'd Items */}
      {subTab === 'eightysix' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Toggle items as unavailable for today's service</p>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={eightySixSearch}
              onChange={(e) => setEightySixSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
              placeholder="Search items..."
            />
          </div>

          {eightySixLoading ? <LoadingSpinner /> : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              {filteredEightySixItems.map((item: any) => (
                <label
                  key={item.id}
                  className={clsx(
                    'flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors',
                    item.isAvailable ? 'bg-slate-50 hover:bg-slate-100' : 'bg-red-50 border border-red-200'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span className={clsx('font-medium text-sm', !item.isAvailable && 'text-red-700 line-through')}>
                      {item.name}
                    </span>
                    {!item.isAvailable && (
                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-medium">86'd</span>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={item.isAvailable !== false}
                    onChange={(e) => toggleEightySixMutation.mutate({ menuItemId: item.id, isAvailable: e.target.checked })}
                    className="w-4 h-4 accent-indigo-600"
                  />
                </label>
              ))}
            </div>
          )}

          {eightySixData && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>{eightySixData.filter((i: any) => !i.isAvailable).length}</strong> items currently 86'd
              </p>
            </div>
          )}
        </div>
      )}

      {/* Announcements */}
      {subTab === 'announcements' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Create announcements for staff pre-shift briefings</p>
            <button onClick={() => setShowAddAnnouncement(true)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> New
            </button>
          </div>

          {announcementsLoading ? <LoadingSpinner /> : !announcementsData?.length ? (
            <EmptyState icon={Megaphone} title="No Announcements" description="Create announcements for your team" />
          ) : (
            <div className="space-y-2">
              {announcementsData.map((ann: any) => {
                const priorityOption = PRIORITY_OPTIONS.find(p => p.value === ann.priority);
                return (
                  <div key={ann.id} className={clsx('p-4 rounded-xl border', ann.isActive ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60')}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{ann.title}</span>
                          <span className={clsx('text-xs px-2 py-0.5 rounded-full border', priorityOption?.color)}>{priorityOption?.label}</span>
                        </div>
                        <p className="text-sm text-slate-600">{ann.message}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          {ann.targetRoles.length === 0 ? 'All roles' : ann.targetRoles.map((r: string) => ROLE_LABELS[r]).join(', ')}
                        </p>
                      </div>
                      <button onClick={() => confirm('Delete announcement?') && deleteAnnouncementMutation.mutate(ann.id)} className="p-1.5 text-slate-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Announcement Modal */}
          {showAddAnnouncement && (
            <Modal onClose={() => setShowAddAnnouncement(false)} title="New Announcement">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={announcementForm.title}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="e.g., VIP guest tonight"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                  <textarea
                    value={announcementForm.message}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                    rows={3}
                    placeholder="Enter details..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <div className="flex gap-2">
                    {PRIORITY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setAnnouncementForm({ ...announcementForm, priority: opt.value })}
                        className={clsx('px-3 py-1.5 text-sm rounded-lg border transition-colors', announcementForm.priority === opt.value ? opt.color : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Target Roles <span className="text-slate-400 font-normal">(empty = all)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {ROLES.map((role) => (
                      <button
                        key={role}
                        onClick={() => {
                          const newRoles = announcementForm.targetRoles.includes(role)
                            ? announcementForm.targetRoles.filter(r => r !== role)
                            : [...announcementForm.targetRoles, role];
                          setAnnouncementForm({ ...announcementForm, targetRoles: newRoles });
                        }}
                        className={clsx('px-3 py-1.5 text-sm rounded-lg border transition-colors', announcementForm.targetRoles.includes(role) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')}
                      >
                        {ROLE_LABELS[role]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowAddAnnouncement(false)} className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200">
                  Cancel
                </button>
                <button
                  onClick={() => createAnnouncementMutation.mutate(announcementForm)}
                  disabled={!announcementForm.title.trim() || !announcementForm.message.trim() || createAnnouncementMutation.isPending}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  {createAnnouncementMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </Modal>
          )}
        </div>
      )}

      {/* Preview */}
      {subTab === 'preview' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Preview as Role</label>
            <select
              value={previewRole}
              onChange={(e) => setPreviewRole(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>{ROLE_LABELS[role]}</option>
              ))}
            </select>
          </div>

          {previewLoading ? <LoadingSpinner /> : (
            <div className="space-y-4">
              {/* Announcements */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Megaphone className="w-4 h-4 text-indigo-600" />
                  Announcements ({previewData?.announcements?.length || 0})
                </h4>
                {previewData?.announcements?.length ? (
                  <div className="space-y-2">
                    {previewData.announcements.map((ann: any) => (
                      <div key={ann.id} className={clsx('p-3 rounded-lg border', ann.priority === 'urgent' ? 'bg-red-50 border-red-200' : ann.priority === 'high' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200')}>
                        <p className="font-medium text-sm">{ann.title}</p>
                        <p className="text-sm text-slate-600">{ann.message}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-500">No announcements</p>}
              </div>

              {/* Specials */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-indigo-600" />
                  Today's Specials ({previewData?.specials?.length || 0})
                </h4>
                {previewData?.specials?.length ? (
                  <div className="space-y-2">
                    {previewData.specials.map((special: any) => (
                      <div key={special.id} className="p-3 bg-white rounded-lg border border-slate-200">
                        <div className="flex justify-between">
                          <span className="font-medium text-sm">{special.menuItem?.name}</span>
                          <span className="text-sm text-indigo-600 font-medium">
                            ${((special.specialPriceCents || special.menuItem?.priceCents || 0) / 100).toFixed(2)}
                          </span>
                        </div>
                        {special.description && <p className="text-sm text-slate-500 mt-1">{special.description}</p>}
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-500">No specials today</p>}
              </div>

              {/* 86'd Items */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  86'd Items ({previewData?.eightySixItems?.length || 0})
                </h4>
                {previewData?.eightySixItems?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {previewData.eightySixItems.map((item: any) => (
                      <span key={item.id} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">{item.name}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-emerald-600 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> All items available
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================
function StatCard({ icon: Icon, label, value, color, subtext, progress }: { icon: any; label: string; value: any; color: string; subtext?: string; progress?: number }) {
  const colors: Record<string, string> = {
    indigo: 'from-indigo-50 to-indigo-100 text-indigo-600',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-600',
    amber: 'from-amber-50 to-amber-100 text-amber-600',
    blue: 'from-blue-50 to-blue-100 text-blue-600',
  };
  const iconColors: Record<string, string> = {
    indigo: 'bg-indigo-100 text-indigo-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
  };
  const progressColors: Record<string, string> = {
    indigo: 'bg-indigo-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    blue: 'bg-blue-500',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-4`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconColors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-600">{label}</p>
        </div>
      </div>
      {subtext && <p className="text-xs text-slate-500 mt-2">{subtext}</p>}
      {progress !== undefined && (
        <div className="mt-3 w-full h-1.5 bg-white/50 rounded-full overflow-hidden">
          <div className={`h-full ${progressColors[color]} transition-all`} style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}

function ViewToggle({ id, label, icon: Icon, current, onChange }: { id: string; label: string; icon: any; current: string; onChange: (id: any) => void }) {
  return (
    <button
      onClick={() => onChange(id)}
      className={clsx(
        'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2',
        current === id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      )}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
    </div>
  );
}

function EmptyState({ icon: Icon, title, description }: { icon: any; title: string; description: string }) {
  return (
    <div className="text-center py-12 bg-slate-50 rounded-xl">
      <Icon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-600 font-medium">{title}</p>
      <p className="text-sm text-slate-500 mt-1">{description}</p>
    </div>
  );
}

function Modal({ onClose, title, children }: { onClose: () => void; title: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, onClick, title, color }: { icon: any; onClick: () => void; title: string; color?: string }) {
  const colorClasses = {
    amber: 'text-amber-600 hover:bg-amber-50',
    red: 'text-red-600 hover:bg-red-50',
    default: 'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
  };
  return (
    <button onClick={onClick} title={title} className={`p-2 rounded-lg transition-colors ${colorClasses[color as keyof typeof colorClasses] || colorClasses.default}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
}

function ShiftStatus({ shift }: { shift: any }) {
  if (shift.isOnBreak) {
    return <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">On Break</span>;
  }
  if (shift.lastBreakHoursAgo >= 4) {
    return <span className="px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Needs Break</span>;
  }
  return <span className="px-2.5 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Working</span>;
}

function FloorPlanView({ sections, onAssign }: { sections: any[]; onAssign: (section: any) => void }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {sections.map((section) => (
        <div key={section.id} className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors" style={{ borderLeftColor: section.color, borderLeftWidth: 4 }}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-slate-900">{section.name}</h4>
            <span className="text-xs text-slate-500">{section.tables?.length || 0} tables</span>
          </div>
          {section.assignedServer ? (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ backgroundColor: section.color }}>
                {section.assignedServer.firstName?.[0]}{section.assignedServer.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-slate-900 truncate">{section.assignedServer.firstName} {section.assignedServer.lastName}</p>
                <p className="text-xs text-slate-500 capitalize">{section.assignedServer.primaryRole}</p>
              </div>
              <button onClick={() => onAssign({ ...section, type: 'section' })} className="text-xs text-indigo-600 hover:text-indigo-700">Reassign</button>
            </div>
          ) : (
            <button onClick={() => onAssign({ ...section, type: 'section' })} className="w-full py-2 border border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-indigo-500 hover:text-indigo-600 text-sm transition-colors">
              + Assign Server
            </button>
          )}
          <div className="mt-3 flex flex-wrap gap-1">
            {section.tables?.map((table: any) => (
              <div key={table.id} className={clsx('w-7 h-7 rounded flex items-center justify-center text-xs font-medium', table.status === 'available' ? 'bg-emerald-100 text-emerald-700' : table.status === 'occupied' || table.status === 'seated' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500')}>
                {table.number}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AssignmentListView({ sections, onAssign }: { sections: any[]; onAssign: (section: any) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Section</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Tables</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Server</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
            <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sections.map((section) => (
            <tr key={section.id} className="hover:bg-slate-50">
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: section.color }} />
                  <span className="font-medium text-slate-900">{section.name}</span>
                </div>
              </td>
              <td className="py-3 px-4 text-sm text-slate-600">{section.tables?.map((t: any) => t.number).join(', ') || '-'}</td>
              <td className="py-3 px-4 text-sm">{section.assignedServer ? `${section.assignedServer.firstName} ${section.assignedServer.lastName}` : <span className="text-slate-400">Unassigned</span>}</td>
              <td className="py-3 px-4">
                <span className={clsx('px-2 py-1 text-xs font-medium rounded-full', section.assignedServer ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
                  {section.assignedServer ? 'Active' : 'Needs Server'}
                </span>
              </td>
              <td className="py-3 px-4 text-right">
                <button onClick={() => onAssign({ ...section, type: 'section' })} className="text-sm text-indigo-600 hover:text-indigo-700">
                  {section.assignedServer ? 'Reassign' : 'Assign'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KitchenView({ stations, onAssign, onUnassign, onCreate }: { stations: any[]; onAssign: (station: any) => void; onUnassign: (stationId: string) => void; onCreate: () => void }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {stations.map((station) => (
        <div key={station.id} className="border border-slate-200 rounded-xl p-4 text-center hover:border-slate-300 transition-colors">
          <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-white" style={{ backgroundColor: station.color || '#6366f1' }}>
            <Flame className="w-5 h-5" />
          </div>
          <h4 className="font-medium text-slate-900 uppercase tracking-wide text-sm">{station.name}</h4>
          {station.assignedCook ? (
            <div className="mt-3">
              <p className="text-sm text-slate-700">{station.assignedCook.firstName} {station.assignedCook.lastName?.[0]}.</p>
              <button onClick={() => onAssign({ ...station, type: 'station' })} className="mt-2 text-xs text-indigo-600 hover:text-indigo-700">Reassign</button>
            </div>
          ) : (
            <button onClick={() => onAssign({ ...station, type: 'station' })} className="mt-3 w-full py-2 border border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-indigo-500 hover:text-indigo-600 text-sm transition-colors">
              Assign
            </button>
          )}
        </div>
      ))}
      <button onClick={onCreate} className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:border-indigo-500 hover:text-indigo-600 transition-colors flex flex-col items-center justify-center min-h-[140px]">
        <Plus className="w-6 h-6 mb-2" />
        <span className="text-sm font-medium">Add Station</span>
      </button>
    </div>
  );
}

function UnassignedAlert({ type, staff }: { type: 'servers' | 'cooks'; staff: any[] }) {
  const config = {
    servers: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', icon: User, iconColor: 'text-amber-600' },
    cooks: { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-800', icon: ChefHat, iconColor: 'text-orange-600' },
  };
  const { bg, text, icon: Icon, iconColor } = config[type];

  return (
    <div className={`${bg} border rounded-xl p-4`}>
      <h4 className={`font-medium ${text} mb-2`}>Unassigned {type === 'servers' ? 'Servers' : 'Cooks'} ({staff.length})</h4>
      <div className="flex flex-wrap gap-2">
        {staff.map((person) => (
          <div key={person.id} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-amber-300 rounded-full">
            <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
            <span className="text-sm text-slate-700">{person.firstName} {person.lastName}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// UTILITY FUNCTIONS
// ============================================
function formatCurrency(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatHours(hours: number) {
  return `${hours.toFixed(1)}h`;
}

// For genuine instants (clock-in/out) — display in the viewer's local zone.
function formatTime(isoString: string) {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// For SHIFT times (instance start/end, assignment scheduledStart/End) — stored
// as the UTC clock-face, so read UTC to show the same time to every viewer.
function formatShiftTime(isoString: string) {
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

// BOH (Kitchen) = warm amber, FOH (Floor) = cool slate
const BOH_ROLES = new Set(['cook', 'dishwasher']);

function getRoleColor(role: string) {
  const normalizedRole = role?.toLowerCase() || '';
  // Kitchen staff: warm amber, Floor staff: cool slate
  return BOH_ROLES.has(normalizedRole) ? '#d97706' : '#475569';
}

// ============================================
// CALENDAR TAB (New shift-centric view)
// ============================================
function CalendarTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Format date as YYYY-MM-DD (using local time, not UTC)
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse YYYY-MM-DD string as local date (not UTC)
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Get current week start/end
  const getWeekBounds = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const start = new Date(d.setDate(diff));
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return {
      startDate: formatLocalDate(start),
      endDate: formatLocalDate(end),
    };
  };

  const [dateRange, setDateRange] = useState(() => getWeekBounds(new Date()));
  const [selectedInstance, setSelectedInstance] = useState<ShiftInstance | null>(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createDate, setCreateDate] = useState<string | null>(null);
  // Custom shift creation state
  const [customShiftName, setCustomShiftName] = useState('');
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');

  // Fetch shift instances for the date range
  const { data: instancesData, isLoading: isLoadingInstances } = useQuery({
    queryKey: ['shift-instances', dateRange.startDate, dateRange.endDate],
    queryFn: () => shiftInstancesApi.getByDateRange(dateRange.startDate, dateRange.endDate),
  });

  // Fetch sections for detail panel
  const { data: sectionsData } = useQuery({
    queryKey: ['sections'],
    queryFn: () => sectionsApi.getAll(),
  });

  // Fetch available employees when panel is open
  const { data: availableEmployeesData, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['available-employees', selectedInstance?.id],
    queryFn: async () => {
      if (!selectedInstance) return { data: { data: [] } };
      return shiftInstancesApi.getAvailableEmployees(selectedInstance.id);
    },
    enabled: !!selectedInstance && showDetailPanel,
  });

  // Fetch templates for quick creation
  const { data: templatesData } = useQuery({
    queryKey: ['shift-templates', { isActive: true }],
    queryFn: () => shiftTemplatesApi.getAll({ isActive: true }),
  });

  // Deduplicate instances to prevent showing same shift multiple times
  // Use a composite key of date+name+startTime to catch true duplicates, not just ID duplicates
  const instances: ShiftInstance[] = useMemo(() => {
    const rawInstances = instancesData?.data?.data || [];
    const seenIds = new Set<string>();
    const seenCompositeKeys = new Set<string>();

    // Log for debugging
    if (rawInstances.length > 0) {
      console.log(`[ShiftsCalendar] Raw instances from API: ${rawInstances.length}`);
    }

    const deduplicated = rawInstances.filter((instance: ShiftInstance) => {
      // First check by ID
      if (seenIds.has(instance.id)) {
        console.log(`[ShiftsCalendar] Duplicate ID: ${instance.id}`);
        return false;
      }
      seenIds.add(instance.id);

      // Then check by composite key (date + name + startTime) to catch true duplicates
      const compositeKey = `${instance.date}|${instance.name}|${instance.startTime}`;
      if (seenCompositeKeys.has(compositeKey)) {
        console.log(`[ShiftsCalendar] Duplicate composite key: ${compositeKey}, ID: ${instance.id}`);
        return false;
      }
      seenCompositeKeys.add(compositeKey);

      return true;
    });

    if (deduplicated.length !== rawInstances.length) {
      console.log(`[ShiftsCalendar] Removed ${rawInstances.length - deduplicated.length} duplicates, showing ${deduplicated.length}`);
    }

    return deduplicated;
  }, [instancesData]);

  const sections = sectionsData?.data?.data || [];
  const availableEmployees = (availableEmployeesData as any)?.data?.data || [];
  const templates: ShiftTemplate[] = templatesData?.data?.data || [];

  // Mutations
  const addAssignmentMutation = useMutation({
    mutationFn: ({ instanceId, data }: { instanceId: string; data: AddAssignmentData }) =>
      shiftInstancesApi.addAssignment(instanceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
      queryClient.invalidateQueries({ queryKey: ['available-employees'] });
    },
  });

  const removeAssignmentMutation = useMutation({
    mutationFn: ({ instanceId, assignmentId }: { instanceId: string; assignmentId: string }) =>
      shiftInstancesApi.removeAssignment(instanceId, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
      queryClient.invalidateQueries({ queryKey: ['available-employees'] });
    },
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: ({ instanceId, assignmentId, data }: { instanceId: string; assignmentId: string; data: { sectionId?: string | null; tableIds?: string[] } }) =>
      shiftInstancesApi.updateAssignment(instanceId, assignmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
    },
  });

  const updateInstanceMutation = useMutation({
    mutationFn: ({ instanceId, data }: { instanceId: string; data: { notes?: string; status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' } }) =>
      shiftInstancesApi.update(instanceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
    },
  });

  const createInstanceMutation = useMutation({
    mutationFn: (data: any) => shiftInstancesApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
      setShowCreateModal(false);
      setCreateDate(null);
      // Navigate to setup page for the new shift
      const newShiftId = response?.data?.data?.id || response?.data?.id;
      console.log('Shift created successfully:', response?.data);
      if (newShiftId) {
        navigate(`/shifts/${newShiftId}/setup`);
      } else {
        console.warn('No shift ID in response:', response);
      }
    },
    onError: (error: any) => {
      console.error('Failed to create shift:', error);
      const errorData = error?.response?.data?.error;
      if (errorData?.code === 'DUPLICATE_SHIFT') {
        alert(errorData.message);
      } else {
        alert(`Failed to create shift: ${errorData?.message || error.message}`);
      }
    },
  });

  // Delete instance mutation
  const deleteInstanceMutation = useMutation({
    mutationFn: (instanceId: string) => shiftInstancesApi.delete(instanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
      setShowDetailPanel(false);
      setSelectedInstance(null);
    },
    onError: (error: any) => {
      console.error('Failed to delete shift:', error);
      alert(`Failed to delete shift: ${error?.response?.data?.error?.message || error.message}`);
    },
  });

  // Cleanup duplicates mutation
  const cleanupMutation = useMutation({
    mutationFn: () => shiftInstancesApi.cleanupDuplicates(),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
      const details = response?.data?.details;
      alert(`Cleanup complete!\n\nRemoved ${details?.duplicatesRemoved || 0} duplicate shifts.\nRemaining shifts: ${details?.remainingInstances || 0}`);
    },
    onError: (error: any) => {
      console.error('Cleanup failed:', error);
      alert(`Cleanup failed: ${error?.response?.data?.error?.message || error.message}`);
    },
  });

  const handleCleanupDuplicates = () => {
    if (confirm('This will permanently remove duplicate shift instances.\n\nShifts with the same date, name, and start time will be deduplicated (keeping the one with the most staff assignments).\n\nContinue?')) {
      cleanupMutation.mutate();
    }
  };

  // Handlers
  const handleShiftClick = (instance: ShiftInstance) => {
    setSelectedInstance(instance);
    setShowDetailPanel(true);
  };

  const handleAddShift = (date: string) => {
    setCreateDate(date);
    setShowCreateModal(true);
  };

  const handleWeekChange = useCallback((startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
  }, []);

  const handleAddAssignment = async (instanceId: string, data: AddAssignmentData) => {
    await addAssignmentMutation.mutateAsync({ instanceId, data });
    // Refresh the selected instance
    const refreshed = instances.find(i => i.id === instanceId);
    if (refreshed) {
      setSelectedInstance(refreshed);
    }
  };

  const handleRemoveAssignment = async (instanceId: string, assignmentId: string) => {
    await removeAssignmentMutation.mutateAsync({ instanceId, assignmentId });
  };

  const handleUpdateInstance = async (instanceId: string, data: { notes?: string; status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' }) => {
    await updateInstanceMutation.mutateAsync({ instanceId, data });
  };

  const handleUpdateAssignment = async (instanceId: string, assignmentId: string, data: { sectionId?: string | null; tableIds?: string[] }) => {
    await updateAssignmentMutation.mutateAsync({ instanceId, assignmentId, data });
  };

  const handleDeleteShift = async (instanceId: string) => {
    await deleteInstanceMutation.mutateAsync(instanceId);
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    if (!createDate) return;
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    const { startTime, endTime } = wallClockRangeToISO(createDate, template.startTime, template.endTime);

    await createInstanceMutation.mutateAsync({
      templateId: template.id,
      date: createDate,
      startTime,
      endTime,
      name: template.name,
      color: template.color,
      activeSectionIds: template.activeSectionIds,
      roleRequirements: template.roleRequirements,
    });
  };

  // Quick create without template
  const handleQuickCreate = async (name: string, startTimeStr: string, endTimeStr: string) => {
    if (!createDate) return;

    const { startTime, endTime } = wallClockRangeToISO(createDate, startTimeStr, endTimeStr);

    // Default role requirements for a typical service
    const defaultRoleRequirements = [
      { role: 'server', minCount: 2 },
      { role: 'host', minCount: 1 },
      { role: 'cook', minCount: 2 },
      { role: 'bartender', minCount: 1 },
    ];

    await createInstanceMutation.mutateAsync({
      date: createDate,
      startTime,
      endTime,
      name,
      color: name.includes('Lunch') ? '#F59E0B' : '#6366F1',
      activeSectionIds: sections.map((s: any) => s.id),
      roleRequirements: defaultRoleRequirements,
    });
  };

  const handleCustomCreate = async () => {
    if (!createDate || !customShiftName.trim() || !customStartTime || !customEndTime) return;

    const startTime = new Date(`${createDate}T${customStartTime}:00`);
    const endTime = new Date(`${createDate}T${customEndTime}:00`);
    if (endTime <= startTime) {
      endTime.setDate(endTime.getDate() + 1);
    }

    const defaultRoleRequirements = [
      { role: 'server', minCount: 2 },
      { role: 'host', minCount: 1 },
      { role: 'cook', minCount: 2 },
      { role: 'bartender', minCount: 1 },
    ];

    await createInstanceMutation.mutateAsync({
      date: createDate,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      name: customShiftName.trim(),
      color: '#3B82F6',
      activeSectionIds: sections.map((s: any) => s.id),
      roleRequirements: defaultRoleRequirements,
    });

    // Reset custom fields
    setCustomShiftName('');
    setCustomStartTime('');
    setCustomEndTime('');
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateDate(null);
    setCustomShiftName('');
    setCustomStartTime('');
    setCustomEndTime('');
  };

  return (
    <div className="space-y-4">
      {/* Quick actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          Click on a shift to view details and manage staff assignments
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCleanupDuplicates}
            disabled={cleanupMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
            title="Remove duplicate shifts"
          >
            {cleanupMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Cleanup</span>
          </button>
          <button
            onClick={() => navigate('/schedule-builder')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">Build Schedule</span>
          </button>
          <button
            onClick={() => {
              setCreateDate(new Date().toISOString().split('T')[0]);
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Shift
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="relative">
        {isLoadingInstances && (
          <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        )}
        <ShiftCalendar
          instances={instances}
          onShiftClick={handleShiftClick}
          onAddShift={handleAddShift}
          onDateRangeChange={handleWeekChange}
        />
      </div>

      {/* Detail Panel */}
      <ShiftDetailPanel
        instance={selectedInstance}
        isOpen={showDetailPanel}
        onClose={() => {
          setShowDetailPanel(false);
          setSelectedInstance(null);
        }}
        onAddAssignment={handleAddAssignment}
        onRemoveAssignment={handleRemoveAssignment}
        onUpdateAssignment={handleUpdateAssignment}
        onUpdateInstance={handleUpdateInstance}
        onDeleteShift={handleDeleteShift}
        availableEmployees={availableEmployees}
        sections={sections}
        isLoadingEmployees={isLoadingEmployees}
        isDeletingShift={deleteInstanceMutation.isPending}
      />

      {/* Quick Create Modal */}
      {showCreateModal && createDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full m-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Add Shift
              </h2>
              <button
                onClick={closeCreateModal}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Date Picker */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                <input
                  type="date"
                  value={createDate}
                  onChange={(e) => setCreateDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>
              {/* Quick Create Options */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Quick Create</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleQuickCreate('Lunch Service', '11:00', '15:00')}
                    disabled={createInstanceMutation.isPending}
                    className="p-3 text-left bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-amber-600" />
                      <span className="font-medium text-amber-800">Lunch</span>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">11:00 AM - 3:00 PM</p>
                  </button>
                  <button
                    onClick={() => handleQuickCreate('Dinner Service', '17:00', '23:00')}
                    disabled={createInstanceMutation.isPending}
                    className="p-3 text-left bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center gap-2">
                      <Moon className="w-4 h-4 text-indigo-600" />
                      <span className="font-medium text-indigo-800">Dinner</span>
                    </div>
                    <p className="text-xs text-indigo-600 mt-1">5:00 PM - 11:00 PM</p>
                  </button>
                </div>
              </div>

              {/* Custom Shift */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Custom Shift</p>
                <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <input
                    type="text"
                    value={customShiftName}
                    onChange={(e) => setCustomShiftName(e.target.value)}
                    placeholder="Shift name (e.g., Brunch Service)"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Start Time</label>
                      <input
                        type="time"
                        value={customStartTime}
                        onChange={(e) => setCustomStartTime(e.target.value)}
                        placeholder="09:00"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">End Time</label>
                      <input
                        type="time"
                        value={customEndTime}
                        onChange={(e) => setCustomEndTime(e.target.value)}
                        placeholder="17:00"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 -mt-1">24h format (e.g., 09:00 for 9 AM, 17:00 for 5 PM)</p>
                  <button
                    onClick={handleCustomCreate}
                    disabled={createInstanceMutation.isPending || !customShiftName.trim() || !customStartTime || !customEndTime}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {createInstanceMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Create Shift
                  </button>
                </div>
              </div>

              {/* Templates */}
              {templates.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">From Template</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleCreateFromTemplate(template.id)}
                        disabled={createInstanceMutation.isPending}
                        className="w-full flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: template.color || '#3B82F6' }}
                          />
                          <div className="text-left">
                            <p className="font-medium text-slate-900">{template.name}</p>
                            <p className="text-xs text-slate-500">
                              {template.startTime} - {template.endTime}
                            </p>
                          </div>
                        </div>
                        {createInstanceMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                        ) : (
                          <Plus className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {createInstanceMutation.isPending && (
                <div className="flex items-center justify-center gap-2 py-2 text-indigo-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Creating shift...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// TEMPLATES TAB (Manage shift templates)
// ============================================
function TemplatesTab() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ShiftTemplate | null>(null);
  const [generatingTemplateId, setGeneratingTemplateId] = useState<string | null>(null);
  const [generateStartDate, setGenerateStartDate] = useState('');
  const [generateEndDate, setGenerateEndDate] = useState('');

  // Fetch templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['shift-templates'],
    queryFn: () => shiftTemplatesApi.getAll(),
  });

  // Fetch sections
  const { data: sectionsData } = useQuery({
    queryKey: ['sections'],
    queryFn: () => sectionsApi.getAll(),
  });

  const templates: ShiftTemplate[] = templatesData?.data?.data || [];
  const sections = sectionsData?.data?.data || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateShiftTemplateData) => shiftTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-templates'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateShiftTemplateData> }) =>
      shiftTemplatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-templates'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => shiftTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-templates'] });
    },
  });

  const generateMutation = useMutation({
    mutationFn: (data: { templateId: string; startDate: string; endDate: string }) =>
      shiftInstancesApi.generateFromTemplate(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
      setGeneratingTemplateId(null);
      setGenerateStartDate('');
      setGenerateEndDate('');
      alert(`Created ${result.data?.data?.length || 0} shift instances`);
    },
  });

  const handleSave = async (data: CreateShiftTemplateData) => {
    if (editingTemplate) {
      await updateMutation.mutateAsync({ id: editingTemplate.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
    setShowModal(false);
    setEditingTemplate(null);
  };

  const handleEdit = (template: ShiftTemplate) => {
    setEditingTemplate(template);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this template?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleGenerate = async () => {
    if (!generatingTemplateId || !generateStartDate || !generateEndDate) return;
    await generateMutation.mutateAsync({
      templateId: generatingTemplateId,
      startDate: generateStartDate,
      endDate: generateEndDate,
    });
  };

  // Day labels
  const DAY_LABELS: Record<number, string> = {
    0: 'Sun',
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Shift Templates</h2>
          <p className="text-sm text-slate-500">Define reusable shift patterns with staffing requirements</p>
        </div>
        <button
          onClick={() => {
            setEditingTemplate(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </div>

      {/* Templates list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No templates yet</p>
          <p className="text-sm text-slate-500 mt-1">Create a template to define recurring shift patterns</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: template.color || '#3B82F6' }}
                  />
                  <div>
                    <h3 className="font-semibold text-slate-900">{template.name}</h3>
                    <p className="text-sm text-slate-500">
                      {template.startTime} - {template.endTime}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(template)}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Repeat days */}
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                  <span
                    key={day}
                    className={clsx(
                      'w-7 h-7 flex items-center justify-center text-xs font-medium rounded',
                      template.repeatDays.includes(day)
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-slate-100 text-slate-400'
                    )}
                  >
                    {DAY_LABELS[day]}
                  </span>
                ))}
              </div>

              {/* Role requirements */}
              {template.roleRequirements.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {template.roleRequirements.map((req, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full"
                    >
                      {req.minCount} {ROLE_LABELS[req.role] || req.role}
                    </span>
                  ))}
                </div>
              )}

              {/* Generate button */}
              {generatingTemplateId === template.id ? (
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <input
                    type="date"
                    value={generateStartDate}
                    onChange={(e) => setGenerateStartDate(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded"
                  />
                  <span className="text-slate-400">to</span>
                  <input
                    type="date"
                    value={generateEndDate}
                    onChange={(e) => setGenerateEndDate(e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded"
                  />
                  <button
                    onClick={handleGenerate}
                    disabled={!generateStartDate || !generateEndDate || generateMutation.isPending}
                    className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {generateMutation.isPending ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      'Go'
                    )}
                  </button>
                  <button
                    onClick={() => setGeneratingTemplateId(null)}
                    className="p-1 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setGeneratingTemplateId(template.id)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Generate Shifts
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Template Modal */}
      <ShiftTemplateModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingTemplate(null);
        }}
        onSave={handleSave}
        template={editingTemplate}
        sections={sections}
      />
    </div>
  );
}

// ============================================
// HISTORY TAB - Completed shifts & reports
// ============================================
function HistoryTab() {
  const [dateRange, setDateRange] = useState<'7d' | '14d' | '30d' | 'custom'>('7d');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [expandedShift, setExpandedShift] = useState<string | null>(null);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  const toggleDate = (dateKey: string) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };


  // Update dates when range changes
  const handleRangeChange = (range: '7d' | '14d' | '30d' | 'custom') => {
    setDateRange(range);
    if (range !== 'custom') {
      const days = range === '7d' ? 7 : range === '14d' ? 14 : 30;
      const start = new Date();
      start.setDate(start.getDate() - days);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(new Date().toISOString().split('T')[0]);
    }
  };

  // Fetch shift history
  const { data: historyData, isLoading } = useQuery({
    queryKey: ['shift-history', startDate, endDate, selectedEmployee],
    queryFn: async () => {
      const params: Record<string, string> = { startDate, endDate };
      if (selectedEmployee) params.employeeId = selectedEmployee;
      const response = await staffApi.getShiftHistory(params);
      return response.data;
    },
  });

  // Fetch employees for filter
  const { data: employeesData } = useQuery({
    queryKey: ['staff'],
    queryFn: async () => {
      const response = await staffApi.getAll();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  const formatDuration = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const summary = historyData?.data?.summary;
  const shifts = historyData?.data?.shifts || [];
  const employeeStats = historyData?.data?.employeeStats ? Object.entries(historyData.data.employeeStats) : [];

  // Group shifts by date
  const shiftsByDate = shifts.reduce((acc: Record<string, any[]>, shift: any) => {
    const dateKey = new Date(shift.clockIn).toISOString().split('T')[0];
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(shift);
    return acc;
  }, {});

  // Sort dates descending and sort shifts within each date by clock-in time descending
  const sortedDates = Object.keys(shiftsByDate).sort((a, b) => b.localeCompare(a));
  sortedDates.forEach(date => {
    shiftsByDate[date].sort((a: any, b: any) =>
      new Date(b.clockIn).getTime() - new Date(a.clockIn).getTime()
    );
  });

  const allDatesExpanded = sortedDates.length > 0 && sortedDates.every(date => expandedDates.has(date));

  const toggleAllDates = () => {
    if (allDatesExpanded) {
      setExpandedDates(new Set());
    } else {
      setExpandedDates(new Set(sortedDates));
    }
  };

  const formatDateHeader = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  // Compute daily digest for each date
  const getDailyDigest = (dateShifts: any[]) => {
    const digest = {
      totalShifts: dateShifts.length,
      totalHours: 0,
      totalTips: 0,
      totalSales: 0,
      tablesServed: 0,
      guestsServed: 0,
      issues: [] as any[],
      highlights: [] as any[],
      employees: new Set<string>(),
      roles: new Set<string>(),
      // Analytics
      avgTipPerGuest: 0,
      avgCheckPerTable: 0,
      avgHoursPerShift: 0,
      topPerformer: null as { name: string; tips: number } | null,
      shiftsWithIssues: 0,
      shiftsWithReports: 0,
      peakHours: { start: '', end: '' },
      issueCategories: {} as Record<string, number>,
    };

    let earliestClockIn = '';
    let latestClockOut = '';
    let maxTips = 0;

    dateShifts.forEach(shift => {
      digest.totalHours += Number(shift.hoursWorked || 0);
      digest.totalTips += shift.tipsTotalCents || 0;
      digest.totalSales += shift.salesTotalCents || 0;
      digest.tablesServed += shift.tablesServed || 0;
      digest.guestsServed += shift.guestsServed || 0;
      digest.employees.add(shift.employeeName);
      if (shift.role) digest.roles.add(shift.role);

      // Track peak hours
      if (!earliestClockIn || shift.clockIn < earliestClockIn) earliestClockIn = shift.clockIn;
      if (!latestClockOut || shift.clockOut > latestClockOut) latestClockOut = shift.clockOut;

      // Track top performer
      if (shift.tipsTotalCents > maxTips) {
        maxTips = shift.tipsTotalCents;
        digest.topPerformer = { name: shift.employeeName, tips: shift.tipsTotalCents };
      }

      // Track reports
      if (shift.postShiftReport) {
        digest.shiftsWithReports++;
        if (shift.postShiftReport.issuesReported?.length > 0) {
          digest.shiftsWithIssues++;
          shift.postShiftReport.issuesReported.forEach((issue: any) => {
            digest.issues.push(issue);
            // Categorize issues
            const issueText = typeof issue === 'string' ? issue.toLowerCase() : (issue?.description || '').toLowerCase();
            if (issueText.includes('pos') || issueText.includes('terminal') || issueText.includes('system')) {
              digest.issueCategories['Technology'] = (digest.issueCategories['Technology'] || 0) + 1;
            } else if (issueText.includes('supply') || issueText.includes('napkin') || issueText.includes('stock') || issueText.includes('low')) {
              digest.issueCategories['Supplies'] = (digest.issueCategories['Supplies'] || 0) + 1;
            } else if (issueText.includes('customer') || issueText.includes('complaint') || issueText.includes('wait')) {
              digest.issueCategories['Service'] = (digest.issueCategories['Service'] || 0) + 1;
            } else {
              digest.issueCategories['Other'] = (digest.issueCategories['Other'] || 0) + 1;
            }
          });
        }
        if (shift.postShiftReport.highlights?.length > 0) {
          digest.highlights.push(...shift.postShiftReport.highlights);
        }
      }
    });

    // Calculate averages
    digest.avgHoursPerShift = digest.totalShifts > 0 ? digest.totalHours / digest.totalShifts : 0;
    digest.avgTipPerGuest = digest.guestsServed > 0 ? digest.totalTips / digest.guestsServed : 0;
    digest.avgCheckPerTable = digest.tablesServed > 0 ? digest.totalSales / digest.tablesServed : 0;

    // Format peak hours
    if (earliestClockIn && latestClockOut) {
      digest.peakHours = {
        start: new Date(earliestClockIn).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        end: new Date(latestClockOut).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      };
    }

    return digest;
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Date Range Quick Select */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700">Period:</span>
            <div className="flex gap-1">
              {(['7d', '14d', '30d', 'custom'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => handleRangeChange(range)}
                  className={clsx(
                    'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                    dateRange === range
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  )}
                >
                  {range === 'custom' ? 'Custom' : range.replace('d', ' days')}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Inputs */}
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
              />
              <span className="text-slate-400">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
              />
            </div>
          )}

          {/* Employee Filter */}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm font-medium text-slate-700">Employee:</span>
            <select
              value={selectedEmployee || ''}
              onChange={(e) => setSelectedEmployee(e.target.value || null)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
            >
              <option value="">All Employees</option>
              {employeesData?.map((emp: any) => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">Total Shifts</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{summary.totalShifts}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <User className="w-4 h-4" />
              <span className="text-sm font-medium">Hours Worked</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatDuration(summary.totalHoursWorked)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Coffee className="w-4 h-4" />
              <span className="text-sm font-medium">Break Time</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{Math.round(summary.totalBreakMinutes / 60)}h {summary.totalBreakMinutes % 60}m</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm font-medium">Total Tips</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.totalTipsCents)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Issues Reported</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{summary.shiftsWithIssues}</p>
          </div>
        </div>
      )}

      {/* Employee Stats Table */}
      {employeeStats.length > 0 && !selectedEmployee && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h3 className="font-semibold text-slate-900">Employee Summary</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Employee</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Role</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Shifts</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Hours</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Tips</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Avg/Shift</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employeeStats.map(([empId, stats]: [string, any]) => (
                  <tr key={empId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{stats.name}</td>
                    <td className="px-4 py-3 text-slate-600 capitalize">{stats.role}</td>
                    <td className="px-4 py-3 text-right text-slate-900">{stats.shifts}</td>
                    <td className="px-4 py-3 text-right text-slate-900">{formatDuration(stats.hours)}</td>
                    <td className="px-4 py-3 text-right text-emerald-600 font-medium">{formatCurrency(stats.tips)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {formatCurrency(stats.shifts > 0 ? Math.round(stats.tips / stats.shifts) : 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Shift List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Completed Shifts</h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">{shifts.length} shifts</span>
            {sortedDates.length > 0 && (
              <button
                onClick={toggleAllDates}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
              >
                {allDatesExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Collapse All
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Expand All
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
          </div>
        ) : shifts.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No completed shifts</p>
            <p className="text-sm">Adjust the date range or filters</p>
          </div>
        ) : (
          <div>
            {sortedDates.map((dateKey) => {
              const digest = getDailyDigest(shiftsByDate[dateKey]);
              const isExpanded = expandedDates.has(dateKey);

              return (
              <div key={dateKey} className="border-b border-slate-200 last:border-b-0">
                {/* Date Header - Clickable */}
                <button
                  onClick={() => toggleDate(dateKey)}
                  className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <ChevronDown className={clsx(
                      'w-5 h-5 text-slate-400 transition-transform',
                      isExpanded && 'rotate-180'
                    )} />
                    <div>
                      <p className="font-semibold text-slate-900">{formatDateHeader(dateKey)}</p>
                      <p className="text-xs text-slate-500">{digest.totalShifts} shifts • {digest.employees.size} employees</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-900">{formatDuration(digest.totalHours)}</p>
                      <p className="text-xs text-slate-500">worked</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-emerald-600">{formatCurrency(digest.totalTips)}</p>
                      <p className="text-xs text-slate-500">tips</p>
                    </div>
                    {digest.issues.length > 0 && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                        {digest.issues.length} issues
                      </span>
                    )}
                  </div>
                </button>

                {/* Expanded Content - Digest + Individual Shifts */}
                {isExpanded && (
                  <>
                    {/* Daily Digest Summary */}
                    <div className="px-4 py-4 bg-white border-t border-slate-100">
                      {/* Service Flow Analysis */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Service Flow</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3">
                            <p className="text-xs text-slate-500">Coverage</p>
                            <p className="text-base font-semibold text-slate-900">{digest.peakHours.start} – {digest.peakHours.end}</p>
                          </div>
                          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3">
                            <p className="text-xs text-slate-500">Staff On Floor</p>
                            <p className="text-base font-semibold text-slate-900">{digest.employees.size} people</p>
                            <p className="text-xs text-slate-400">{formatDuration(digest.avgHoursPerShift)} avg shift</p>
                          </div>
                          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3">
                            <p className="text-xs text-slate-500">Tables / Guests</p>
                            <p className="text-base font-semibold text-slate-900">{digest.tablesServed} / {digest.guestsServed}</p>
                            <p className="text-xs text-slate-400">{digest.tablesServed > 0 ? (digest.guestsServed / digest.tablesServed).toFixed(1) : 0} guests/table</p>
                          </div>
                          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3">
                            <p className="text-xs text-slate-500">Report Rate</p>
                            <p className="text-base font-semibold text-slate-900">{digest.totalShifts > 0 ? Math.round((digest.shiftsWithReports / digest.totalShifts) * 100) : 0}%</p>
                            <p className="text-xs text-slate-400">{digest.shiftsWithReports}/{digest.totalShifts} shifts</p>
                          </div>
                        </div>
                      </div>

                      {/* Revenue & Tips Analysis */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Revenue Analysis</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3">
                            <p className="text-xs text-emerald-600">Total Sales</p>
                            <p className="text-lg font-bold text-emerald-700">{formatCurrency(digest.totalSales)}</p>
                          </div>
                          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-3">
                            <p className="text-xs text-emerald-600">Total Tips</p>
                            <p className="text-lg font-bold text-emerald-700">{formatCurrency(digest.totalTips)}</p>
                          </div>
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3">
                            <p className="text-xs text-blue-600">Avg Check/Table</p>
                            <p className="text-lg font-bold text-blue-700">{formatCurrency(digest.avgCheckPerTable)}</p>
                          </div>
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3">
                            <p className="text-xs text-blue-600">Avg Tip/Guest</p>
                            <p className="text-lg font-bold text-blue-700">{formatCurrency(digest.avgTipPerGuest)}</p>
                          </div>
                        </div>
                        {digest.topPerformer && (
                          <div className="mt-2 px-3 py-2 bg-indigo-50 rounded-lg flex items-center gap-2">
                            <User className="w-4 h-4 text-indigo-600" />
                            <span className="text-sm text-indigo-700">
                              <strong>{digest.topPerformer.name}</strong> led the day with {formatCurrency(digest.topPerformer.tips)} in tips
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Issues Analysis */}
                      {digest.issues.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Issues Analysis</h4>
                          <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                                <span className="text-sm font-medium text-amber-800">
                                  {digest.issues.length} issue{digest.issues.length !== 1 ? 's' : ''} from {digest.shiftsWithIssues} shift{digest.shiftsWithIssues !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                            {/* Issue Categories */}
                            <div className="flex flex-wrap gap-2 mb-3">
                              {Object.entries(digest.issueCategories).map(([category, count]) => (
                                <span key={category} className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                  {category}: {count}
                                </span>
                              ))}
                            </div>
                            {/* Issue List */}
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {digest.issues.map((issue, i) => (
                                <p key={i} className="text-sm text-amber-700 pl-2 border-l-2 border-amber-200">
                                  {typeof issue === 'string' ? issue : (issue as any)?.description || JSON.stringify(issue)}
                                </p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Highlights */}
                      {digest.highlights.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Wins</h4>
                          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                            <div className="flex items-center gap-2 mb-2">
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                              <span className="text-sm font-medium text-emerald-800">
                                {digest.highlights.length} highlight{digest.highlights.length !== 1 ? 's' : ''} reported
                              </span>
                            </div>
                            <div className="space-y-1 max-h-24 overflow-y-auto">
                              {digest.highlights.map((highlight, i) => (
                                <p key={i} className="text-sm text-emerald-700 pl-2 border-l-2 border-emerald-200">
                                  {typeof highlight === 'string' ? highlight : JSON.stringify(highlight)}
                                </p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Individual Shifts for this date */}
                    <div className="divide-y divide-slate-100">
                  {shiftsByDate[dateKey].map((shift: any) => (
                    <div key={shift.id} className="hover:bg-slate-50">
                      {/* Shift Row */}
                      <button
                        onClick={() => setExpandedShift(expandedShift === shift.id ? null : shift.id)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{shift.employeeName}</p>
                            <p className="text-sm text-slate-500">
                              {formatTime(shift.clockIn)} – {formatTime(shift.clockOut)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right min-w-[60px]">
                            <p className="font-medium text-slate-900">{formatDuration(Number(shift.hoursWorked || 0))}</p>
                            <p className="text-xs text-slate-500 capitalize">{shift.role || shift.shiftType}</p>
                          </div>
                          <div className="text-right min-w-[70px]">
                            {shift.tipsTotalCents > 0 ? (
                              <p className="font-medium text-emerald-600">{formatCurrency(shift.tipsTotalCents)}</p>
                            ) : (
                              <p className="text-slate-400">—</p>
                            )}
                          </div>
                          <div className="min-w-[60px] flex justify-end">
                            {shift.postShiftReport?.issuesReported?.length > 0 ? (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                Issues
                              </span>
                            ) : shift.postShiftReport ? (
                              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                                Report
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 bg-slate-100 text-slate-400 text-xs font-medium rounded-full">
                                None
                              </span>
                            )}
                          </div>
                          <ChevronDown className={clsx(
                            'w-5 h-5 text-slate-400 transition-transform',
                            expandedShift === shift.id && 'rotate-180'
                          )} />
                        </div>
                      </button>

                      {/* Expanded Details */}
                      {expandedShift === shift.id && (
                        <div className="px-4 pb-4 bg-slate-50 border-t border-slate-100">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4">
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Clock In</p>
                              <p className="text-sm font-medium">{formatTime(shift.clockIn)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Clock Out</p>
                              <p className="text-sm font-medium">{formatTime(shift.clockOut)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Break Time</p>
                              <p className="text-sm font-medium">{shift.breakMinutes || 0} min</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Shift Type</p>
                              <p className="text-sm font-medium capitalize">{shift.shiftType || shift.role || 'Regular'}</p>
                            </div>
                          </div>

                          {/* Post-Shift Report */}
                          {shift.postShiftReport && (
                            <div className="mt-2 p-3 bg-white rounded-lg border border-slate-200">
                              <p className="text-xs font-medium text-slate-500 mb-2">POST-SHIFT REPORT</p>
                              {shift.postShiftReport.postShiftNotes && (
                                <div className="mb-3">
                                  <p className="text-xs text-slate-500">Notes</p>
                                  <p className="text-sm text-slate-700">{shift.postShiftReport.postShiftNotes}</p>
                                </div>
                              )}
                              {shift.postShiftReport.issuesReported?.length > 0 && (
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">Issues Reported</p>
                                  <ul className="space-y-1">
                                    {(shift.postShiftReport.issuesReported as any[]).map((issue: any, i: number) => (
                                      <li key={i} className="flex items-start gap-2 text-sm text-amber-700">
                                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                        {typeof issue === 'string' ? issue : issue?.description || JSON.stringify(issue)}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {shift.postShiftReport.highlights?.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-slate-500 mb-1">Highlights</p>
                                  <ul className="space-y-1">
                                    {(shift.postShiftReport.highlights as any[]).map((highlight: any, i: number) => (
                                      <li key={i} className="text-sm text-slate-700">
                                        • {typeof highlight === 'string' ? highlight : JSON.stringify(highlight)}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                    </div>
                  </>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
